import { Injectable } from '@angular/core';
import { IStep, TourState } from './ngx-bootstrap-product-tour.models';
import { Observable } from 'rxjs/Observable';
import { map } from 'rxjs/operator/map';
import { mergeStatic } from 'rxjs/operator/merge';
import { Subject } from 'rxjs/Subject';
import'rxjs/add/operator/filter';
import'rxjs/add/operator/first';
import { Router, NavigationStart } from '@angular/router';
import { NgxBootstrapProductTourDirective } from './ngx-bootstrap-product-tour.directive';
export class NgxBootstrapProductTourService {
public stepShow$: Subject<IStep> = new Subject();
public stepHide$: Subject<IStep> = new Subject();
public initialize$: Subject<IStep[]> = new Subject();
public start$: Subject<IStep> = new Subject();
public end$: Subject<any> = new Subject();
public pause$: Subject<IStep> = new Subject();
public resume$: Subject<IStep> = new Subject();
public anchorRegister$: Subject<string> = new Subject();
public anchorUnregister$: Subject<string> = new Subject();
public events$: Observable<{ name: string, value: any }> = mergeStatic(
    map.bind(this.stepShow$)(value => ({ name: 'stepShow', value })),
    map.bind(this.stepHide$)(value => ({ name: 'stepHide', value })),
    map.bind(this.initialize$)(value => ({ name: 'initialize', value })),
    map.bind(this.start$)(value => ({ name: 'start', value })),
    map.bind(this.end$)(value => ({ name: 'end', value })),
    map.bind(this.pause$)(value => ({ name: 'pause', value })),
    map.bind(this.resume$)(value => ({ name: 'resume', value })),
    map.bind(this.anchorRegister$)(value => ({ name: 'anchorRegister', value })),
    map.bind(this.anchorUnregister$)(value => ({ name: 'anchorUnregister', value })),
  );
public steps: IStep[] = [];
public currentStep: IStep;
public anchors: { [anchorId: string]: NgxBootstrapProductTourDirective } = {};
private status: TourState = TourState.OFF;
/**
 * @param {?} router
 */
constructor(private router: Router) { }
/**
 * @param {?} steps
 * @param {?=} stepDefaults
 * @return {?}
 */
public initialize(steps: IStep[], stepDefaults?: IStep): void {
    if (steps && steps.length > 0 && this.status === TourState.OFF) {
      this.status = TourState.OFF;
      this.steps = steps.map(step => Object.assign({}, stepDefaults, step));
      this.initialize$.next(this.steps);
    }
  }
/**
 * @return {?}
 */
public start(): void {
    this.startAt(0);
  }
/**
 * @param {?} stepId
 * @return {?}
 */
public startAt(stepId: number | string): void {
    this.status = TourState.ON;
    this.goToStep(this.loadStep(stepId));
    this.start$.next();
    this.router.events.filter(event => event instanceof NavigationStart).first().subscribe(() => {
      if (this.currentStep) {
        this.hideStep(this.currentStep);
      }
    });
  }
/**
 * @return {?}
 */
public end(): void {
    this.status = TourState.OFF;
    this.hideStep(this.currentStep);
    this.currentStep = undefined;
    this.end$.next();
  }
/**
 * @return {?}
 */
public pause(): void {
    this.status = TourState.PAUSED;
    this.hideStep(this.currentStep);
    this.pause$.next();
  }
/**
 * @return {?}
 */
public resume(): void {
    this.status = TourState.ON;
    this.showStep(this.currentStep);
    this.resume$.next();
  }
/**
 * @param {?=} pause
 * @return {?}
 */
public toggle(pause?: boolean): void {
    if (pause) {
      if (this.currentStep) {
        this.pause();
      } else {
        this.resume();
      }
    } else {
      if (this.currentStep) {
        this.end();
      } else {
        this.start();
      }
    }
  }
/**
 * @return {?}
 */
public next(): void {
    if (this.hasNext(this.currentStep)) {
      this.goToStep(this.loadStep(this.currentStep.nextStep || this.steps.indexOf(this.currentStep) + 1));
    }
  }
/**
 * @param {?} step
 * @return {?}
 */
public hasNext(step: IStep): boolean {
    if (!step) {
      console.warn('Can\'t get next step. No currentStep.');
      return false;
    }
    return step.nextStep !== undefined || this.steps.indexOf(step) < this.steps.length - 1;
  }
/**
 * @return {?}
 */
public prev(): void {
    if (this.hasPrev(this.currentStep)) {
      this.goToStep(this.loadStep(this.currentStep.prevStep || this.steps.indexOf(this.currentStep) - 1));
    }
  }
/**
 * @param {?} step
 * @return {?}
 */
public hasPrev(step: IStep): boolean {
    if (!step) {
      console.warn('Can\'t get previous step. No currentStep.');
      return false;
    }
    return step.prevStep !== undefined || this.steps.indexOf(step) > 0;
  }
/**
 * @param {?} stepId
 * @return {?}
 */
public goto(stepId: number | string): void {
    this.goToStep(this.loadStep(stepId));
  }
/**
 * @param {?} anchorId
 * @param {?} anchor
 * @return {?}
 */
public register(anchorId: string, anchor: NgxBootstrapProductTourDirective): void {
    if (this.anchors[anchorId]) {
      throw new Error('anchorId ' + anchorId + ' already registered!');
    }
    this.anchors[anchorId] = anchor;
    this.anchorRegister$.next(anchorId);
  }
/**
 * @param {?} anchorId
 * @return {?}
 */
public unregister(anchorId: string): void {
    delete this.anchors[anchorId];
    this.anchorUnregister$.next(anchorId);
  }
/**
 * @return {?}
 */
public getStatus(): TourState {
    return this.status;
  }
/**
 * @param {?} step
 * @return {?}
 */
private goToStep(step: IStep): void {
    if (!step) {
      console.warn('Can\'t go to non-existent step');
      this.end();
      return;
    }
    let /** @type {?} */ navigatePromise: Promise<boolean> = new Promise(resolve => resolve(true));
    if (step.route !== undefined && typeof (step.route) === 'string') {
      navigatePromise = this.router.navigateByUrl(step.route);
    } else if (step.route && Array.isArray(step.route)) {
      navigatePromise = this.router.navigate(step.route);
    }

    navigatePromise.then(navigated => {
      if (navigated !== false) {
        setTimeout(() => this.setCurrentStep(step));
      }
    });
  }
/**
 * @param {?} stepId
 * @return {?}
 */
private loadStep(stepId: number | string): IStep {
    if (typeof (stepId) === 'number') {
      return this.steps[stepId];
    } else {
      return this.steps.find(step => step.stepId === stepId);
    }
  }
/**
 * @param {?} step
 * @return {?}
 */
private setCurrentStep(step: IStep): void {
    if (this.currentStep) {
      this.hideStep(this.currentStep);
    }
    this.currentStep = step;

    if (step.promise) {
      step.promise
        .then(() => step.delay ? setTimeout(() => this.showStep(this.currentStep), step.delay) : this.showStep(this.currentStep))
        .catch(() => console.error(`Promise for step ${step.anchorId} has failed!`));

    } else {
      step.delay ? setTimeout(() => this.showStep(this.currentStep), step.delay) : this.showStep(this.currentStep);
    }

    this.router.events.filter(event => event instanceof NavigationStart).first().subscribe(() => {
      if (this.currentStep) {
        this.hideStep(this.currentStep);
      }
    });
  }
/**
 * @param {?} step
 * @return {?}
 */
private showStep(step: IStep): void {
    const /** @type {?} */ anchor = this.anchors[step && step.anchorId];
    if (!anchor) {
      this.end();
      return;
    }
    anchor.showTourStep(step);
    this.stepShow$.next(step);
  }
/**
 * @param {?} step
 * @return {?}
 */
private hideStep(step: IStep): void {
    const /** @type {?} */ anchor = this.anchors[step && step.anchorId];
    if (!anchor) {
      return;
    }
    anchor.hideTourStep();
    this.stepHide$.next(step);
  }
/**
 * @param {?} step
 * @return {?}
 */
public isStepOpen(step) {
    const /** @type {?} */ anchor = this.anchors[step && step.anchorId];
    if (!anchor) {
      return;
    }
    return anchor.isOpen;
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Router, },
];
}

function NgxBootstrapProductTourService_tsickle_Closure_declarations() {
/** @type {?} */
NgxBootstrapProductTourService.decorators;
/**
 * @nocollapse
 * @type {?}
 */
NgxBootstrapProductTourService.ctorParameters;
/** @type {?} */
NgxBootstrapProductTourService.prototype.stepShow$;
/** @type {?} */
NgxBootstrapProductTourService.prototype.stepHide$;
/** @type {?} */
NgxBootstrapProductTourService.prototype.initialize$;
/** @type {?} */
NgxBootstrapProductTourService.prototype.start$;
/** @type {?} */
NgxBootstrapProductTourService.prototype.end$;
/** @type {?} */
NgxBootstrapProductTourService.prototype.pause$;
/** @type {?} */
NgxBootstrapProductTourService.prototype.resume$;
/** @type {?} */
NgxBootstrapProductTourService.prototype.anchorRegister$;
/** @type {?} */
NgxBootstrapProductTourService.prototype.anchorUnregister$;
/** @type {?} */
NgxBootstrapProductTourService.prototype.events$;
/** @type {?} */
NgxBootstrapProductTourService.prototype.steps;
/** @type {?} */
NgxBootstrapProductTourService.prototype.currentStep;
/** @type {?} */
NgxBootstrapProductTourService.prototype.anchors;
/** @type {?} */
NgxBootstrapProductTourService.prototype.status;
/** @type {?} */
NgxBootstrapProductTourService.prototype.router;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
